import { Playbook } from '@/types/playbook/playbook';
import { AlertCircle, Bot, CheckCircle, Clock, Mail, User } from 'lucide-react';

export const statusPageCommunicator: Playbook = {
  meta: {
    id: 'status-page-communicator',
    slug: 'status-page-communicator',
    categoryId: 'provide-support',
    name: 'The Status Page Communicator',
    tagline: 'Manage incidents and downtime proactively.',
    description: 'During outages or maintenance, use a Genie to communicate status updates, set expectations, and gather affected user information while maintaining transparency.',
    strategy: 'During outages or maintenance, use the Genie to communicate status updates, set expectations, and gather affected user information.',
    benefits: [
      'Transparent incident communication',
      'Reduced support ticket volume',
      'Improved customer confidence',
      'Automated status updates'
    ],
    targetAudience: ['SaaS platforms', 'API providers', 'Technology services'],
    estimatedSetupTime: '25 minutes',
    difficulty: 'intermediate',
    tags: ['incident-management', 'status-updates', 'transparency', 'downtime'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Status Page Communicator Playbook - Genie Incident Management',
    metaDescription: 'Manage incidents and downtime proactively with Genie-powered status communication. Keep users informed and reduce support load.',
    keywords: ['status page', 'incident management', 'downtime communication', 'service status', 'Genie communicator']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "⚠️ We're currently experiencing some issues with our payment system. I'm here to help you understand the impact and find alternatives. How can I assist?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "We're experiencing a partial outage. How can I help?" },
    { speaker: 'user', message: "Is the API affected?" },
    { speaker: 'agent', message: "The API is fully operational. Only the payment processing is affected." },
    { speaker: 'user', message: "When will it be fixed?" },
    { speaker: 'agent', message: "Our team expects resolution within 30 minutes. Can I notify you when it's resolved?" }
  ],
  templateConfig: {
    firstMessage: "We're currently experiencing [ISSUE]. I'm here to help you understand the impact and find workarounds.",
    systemPrompt: "You are a status communicator during incidents. Provide clear updates, set realistic expectations, and help users find alternatives. Be transparent and helpful.",
    tools: ['status_updates', 'email_capture', 'ticket_creation']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User visits during incident',
      description: 'Customer accesses service during active outage or maintenance window'
    },
    {
      type: 'genie',
      icon: AlertCircle,
      genieIcon: 'genie-lamp',
      label: 'Immediately informs of status',
      description: 'Genie proactively communicates current incident status and impact scope'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Provides specific details',
      description: 'Explains which services are affected, which are operational, and expected timeline'
    },
    {
      type: 'action',
      icon: Clock,
      genieIcon: 'calendar-check',
      label: 'Sets realistic expectations',
      description: 'Gives accurate timeline estimates and offers to notify when issue is resolved'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures notification preferences',
      description: 'Collects contact information for status updates and resolution notifications'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'check-circle',
      label: 'Maintained customer trust',
      description: 'Transparent communication preserves customer confidence during incidents'
    }
  ],
  metrics: {
    conversionRate: '67%',
    responseTime: 'Immediate',
    satisfactionScore: '4.4/5',
    costSavings: '$20k/incident',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'CloudSync Services',
      authorName: 'David Kim',
      authorTitle: 'Head of Operations',
      quote: 'During our last outage, the Genie handled 80% of user inquiries automatically. Support ticket volume was 60% lower than previous incidents.',
      metrics: '60% reduction in incident tickets'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Incident Response Templates',
      description: 'Pre-written responses for common incident types and scenarios',
      url: '/templates/incident-responses'
    },
    {
      type: 'guide',
      title: 'Status Page Integration',
      description: 'How to connect your status page with the Genie for real-time updates',
      url: '/docs/status-integration'
    }
  ]
};